import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { formatFee } from "@/lib/candidate-utils";
import { Trophy, CurrencyDollar, Clock, Buildings, Users, Warning } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Placements() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/placements?year=${year}`);
      setData(data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) years.push(y);

  const items = data?.placements || [];
  const avgFee = items.length > 0 ? (data.total_fees / items.length) : 0;
  const daysArr = items.map((i) => i.days_to_placement).filter((d) => d !== null && d !== undefined);
  const avgDays = daysArr.length > 0 ? Math.round(daysArr.reduce((s, d) => s + d, 0) / daysArr.length) : "—";

  return (
    <div className="space-y-8" data-testid="placements-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
            <Trophy size={12} weight="bold" /> Placement board
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none">
            Placements
          </h1>
          <div className="text-sm text-zinc-500 mt-3">
            Every placement of the year at a glance — who hired who, for what, and how fast.
          </div>
        </div>
        <div className="flex items-center gap-1" data-testid="year-picker">
          {years.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              data-testid={`year-${y}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border rounded-sm ${
                year === y ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-4 rounded-sm text-sm">{error}</div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="placement-kpis">
        <Kpi label="Placements" value={data?.count ?? 0} icon={Trophy} accent="emerald" />
        <Kpi label="Total fees" value={formatFee(data?.total_fees ?? 0)} icon={CurrencyDollar} accent="emerald" />
        <Kpi label="Avg fee" value={items.length ? formatFee(Math.round(avgFee)) : "—"} icon={CurrencyDollar} />
        <Kpi label="Avg days to placement" value={avgDays} icon={Clock} />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 border-dashed rounded-sm p-16 text-center" data-testid="placements-empty">
          <Trophy size={32} weight="light" className="mx-auto text-zinc-300 mb-4" />
          <div className="font-heading font-bold text-lg mb-1">No placements in {year}</div>
          <div className="text-sm text-zinc-500">
            Placements land here automatically the moment you mark a candidate as hired.
          </div>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="placement-table">
          {/* Header row */}
          <div className="hidden md:grid grid-cols-12 px-5 py-2.5 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            <div className="col-span-3">Candidate</div>
            <div className="col-span-3">Placed with</div>
            <div className="col-span-2 text-right">Fee</div>
            <div className="col-span-2 text-right">Salary</div>
            <div className="col-span-1 text-right">Days</div>
            <div className="col-span-1 text-right">Date</div>
          </div>
          {items.map((p) => (
            <PlacementRow key={p.candidate_id} p={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlacementRow({ p }) {
  const recruiters = p.recruiters || [];
  const fee = Number(p.fee_approx || 0);
  return (
    <Link
      to={`/candidates/${p.candidate_id}`}
      data-testid={`placement-row-${p.candidate_id}`}
      className="grid grid-cols-1 md:grid-cols-12 px-5 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors gap-y-1"
    >
      <div className="md:col-span-3">
        <div className="flex items-center gap-2">
          <div className="font-medium text-sm text-zinc-950 truncate">{p.candidate_name}</div>
          {p.needs_attribution && (
            <span
              title="No placing client set — click to confirm which client hired this candidate"
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-amber-800 bg-amber-50 border border-amber-400 px-1 py-0.5 rounded-sm"
              data-testid={`needs-attribution-${p.candidate_id}`}
            >
              <Warning size={9} weight="bold" /> Needs attribution
            </span>
          )}
        </div>
        {p.role_target && <div className="text-[11px] text-zinc-500 truncate">{p.role_target}</div>}
        {recruiters.length > 0 && (
          <div
            data-testid={`recruiter-splits-${p.candidate_id}`}
            className="mt-1.5 flex flex-wrap items-center gap-1"
          >
            {recruiters.map((r) => {
              const pct = Number(r.split_pct || 0);
              const share = Math.round(fee * (pct / 100));
              const label = recruiters.length === 1 ? r.name : `${r.name} · ${pct}% · ${formatFee(share)}`;
              return (
                <span
                  key={r.user_id || r.name}
                  title={`${r.name} — ${pct}% of ${formatFee(fee)} = ${formatFee(share)}`}
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${
                    recruiters.length > 1
                      ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                      : "border-zinc-200 bg-zinc-50 text-zinc-600"
                  }`}
                >
                  <Users size={9} weight="bold" /> {label}
                </span>
              );
            })}
          </div>
        )}
      </div>
      <div className="md:col-span-3 flex items-center gap-1.5 text-sm text-zinc-800 truncate">
        <Buildings size={12} weight="bold" className="text-zinc-400 flex-shrink-0" />
        {p.placed_client_name || <span className="text-zinc-400 italic">Unknown</span>}
      </div>
      <div className="md:col-span-2 md:text-right text-sm font-medium text-emerald-800">
        <span className="md:hidden text-[10px] uppercase tracking-[0.15em] text-zinc-500 mr-2">Fee</span>
        {formatFee(p.fee_approx || 0)}
      </div>
      <div className="md:col-span-2 md:text-right text-sm text-zinc-700">
        <span className="md:hidden text-[10px] uppercase tracking-[0.15em] text-zinc-500 mr-2">Salary</span>
        {p.offer_salary ? formatFee(p.offer_salary) : <span className="text-zinc-400">—</span>}
      </div>
      <div className="md:col-span-1 md:text-right text-sm text-zinc-700">
        <span className="md:hidden text-[10px] uppercase tracking-[0.15em] text-zinc-500 mr-2">Days</span>
        {p.days_to_placement ?? "—"}
      </div>
      <div className="md:col-span-1 md:text-right text-[11px] text-zinc-500">
        {fmtDate(p.placed_at)}
      </div>
    </Link>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={`placement-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <Icon size={14} weight="bold" className={accent === "emerald" ? "text-emerald-600" : "text-zinc-400"} />
      </div>
      <div className={`font-heading font-black text-3xl tracking-tight ${accent === "emerald" ? "text-emerald-700" : "text-zinc-950"}`}>
        {value}
      </div>
    </div>
  );
}
